import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Upload, message, Image } from 'antd';
import { PlusOutlined, LoadingOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { fileUpload } from '../../../../utils/storageUtils';
import { t } from '../../../../i18n';

interface ImageUploaderEnhanceProps {
    value?: string[];
    onChange?: (keys: string[]) => void;
    onError?: (error: Error, file: File) => void;
    maxCount?: number;
    accept?: string;
    maxSizeMB?: number;
    disabled?: boolean;
    tag?: string;
}

const defaultAccept = '.jpg,.jpeg,.png,.gif';

let _uidSeq = 0;
const nextUid = () => `enhance-${++_uidSeq}`;

const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
    });

const ImageUploaderEnhance: React.FC<ImageUploaderEnhanceProps> = ({
    value = [],
    onChange,
    onError,
    maxCount = 1,
    accept = defaultAccept,
    maxSizeMB,
    disabled = false,
    tag,
}) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const folderInputRef = useRef<HTMLInputElement>(null);
    const fileListRef = useRef<UploadFile[]>([]);

    useEffect(() => { fileListRef.current = fileList; }, [fileList]);

    useEffect(() => {
        if (!value || value.length === 0) {
            setFileList([]);
        }
    }, [value]);

    useEffect(() => {
        return () => {
            fileListRef.current.forEach(f => {
                if (f.url?.startsWith('blob:')) URL.revokeObjectURL(f.url);
            });
        };
    }, []);

    const extractKeys = (list: UploadFile[]): string[] =>
        list.filter(f => f.status === 'done').map(f => f.response?.objectKey || '').filter(Boolean);

    const uploadRawFiles = useCallback(async (files: File[]) => {
        if (disabled || isUploading) return;
        const imageFiles = files.filter(f => f.type.startsWith('image/'));
        if (imageFiles.length === 0) return;

        const remaining = maxCount - fileListRef.current.length;
        if (remaining <= 0) return;

        const toUpload = imageFiles.slice(0, remaining);

        if (maxSizeMB !== undefined) {
            const oversized = toUpload.find(f => f.size / 1024 / 1024 >= maxSizeMB);
            if (oversized) {
                message.error(t('components.upload.imageSizeLimit', { maxSize: maxSizeMB }));
                return;
            }
        }

        setIsUploading(true);
        try {
            const newFiles = await Promise.all(
                toUpload.map(async (file, i): Promise<UploadFile> => {
                    const { objectKey } = await fileUpload(file, tag);
                    const blobUrl = URL.createObjectURL(file);
                    return {
                        uid: nextUid(),
                        name: file.name,
                        status: 'done',
                        url: blobUrl,
                        thumbUrl: blobUrl,
                        response: { objectKey },
                    };
                })
            );
            setFileList(current => {
                const updated = [...current, ...newFiles];
                setTimeout(() => onChange?.(extractKeys(updated)), 0);
                return updated;
            });
        } catch (error) {
            const err = error instanceof Error ? error : new Error(t('components.upload.uploadFailed'));
            message.error(err.message);
            onError?.(err, toUpload[0]);
        } finally {
            setIsUploading(false);
        }
    }, [disabled, isUploading, maxCount, maxSizeMB, tag, onChange, onError]);

    const uploadRawFilesRef = useRef(uploadRawFiles);
    useEffect(() => { uploadRawFilesRef.current = uploadRawFiles; }, [uploadRawFiles]);

    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            const files = Array.from(e.clipboardData?.files || []);
            if (files.length > 0) uploadRawFilesRef.current(files);
        };
        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        uploadRawFiles(Array.from(e.dataTransfer.files));
    }, [disabled, uploadRawFiles]);

    const handleFolderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        uploadRawFiles(Array.from(e.target.files || []));
        e.target.value = '';
    }, [uploadRawFiles]);

    const beforeUpload = useCallback((file: File) => {
        if (!file.type.startsWith('image/')) {
            message.error(t('components.upload.onlyImageFilesAllowed'));
            return Upload.LIST_IGNORE;
        }
        if (maxSizeMB !== undefined && file.size / 1024 / 1024 >= maxSizeMB) {
            message.error(t('components.upload.imageSizeLimit', { maxSize: maxSizeMB }));
            return Upload.LIST_IGNORE;
        }
        return false;
    }, [maxSizeMB]);

    const handleUploadChange: UploadProps['onChange'] = useCallback(async ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
        const toUpload = newFileList.filter((f: UploadFile) => f.originFileObj && f.status !== 'done' && f.status !== 'error');

        if (toUpload.length === 0) {
            setFileList(newFileList);
            onChange?.(extractKeys(newFileList));
            return;
        }

        const pendingList = newFileList.map((f: UploadFile) =>
            (!f.originFileObj || f.status === 'done' || f.status === 'error')
                ? f
                : { ...f, status: 'uploading' as const }
        );
        setFileList(pendingList);
        setIsUploading(true);
        try {
            const updated = await Promise.all(
                newFileList.map(async (file: UploadFile): Promise<UploadFile> => {
                    if (!file.originFileObj || file.status === 'done' || file.status === 'error') return file;
                    try {
                        const { objectKey } = await fileUpload(file.originFileObj, tag);
                        const blobUrl = URL.createObjectURL(file.originFileObj);
                        return { ...file, status: 'done', url: blobUrl, thumbUrl: blobUrl, response: { objectKey } };
                    } catch {
                        return { ...file, status: 'error' };
                    }
                })
            );
            setFileList(updated);
            setTimeout(() => onChange?.(extractKeys(updated)), 0);
        } catch (error) {
            const err = error instanceof Error ? error : new Error(t('components.upload.uploadFailed'));
            message.error(err.message);
        } finally {
            setIsUploading(false);
        }
    }, [tag, onChange]);

    const handleRemove = useCallback((file: UploadFile) => {
        if (file.url?.startsWith('blob:')) URL.revokeObjectURL(file.url);
        return true;
    }, []);

    const handlePreview = useCallback(async (file: UploadFile) => {
        if (!file.url && !file.preview && file.originFileObj) {
            file.preview = await getBase64(file.originFileObj as File);
        }
        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    }, []);

    const renderUploadItem = useCallback<NonNullable<UploadProps['itemRender']>>(
        (_originNode, file, _fileList, actions) => {
            if (file.status && file.status !== 'done' && file.status !== 'error') return _originNode;
            const fileUrl = file.url || file.thumbUrl;
            return (
                <div className="relative w-full h-full rounded border border-gray-200 overflow-hidden bg-gray-100 group">
                    {fileUrl && <img src={fileUrl} alt={file.name} className="w-full h-full object-cover" />}
                    <button
                        type="button"
                        className="absolute inset-0 flex items-center justify-center text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity bg-black/40"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePreview(file); }}
                        aria-label={t('components.upload.preview') || 'Preview'}
                    >
                        <EyeOutlined />
                    </button>
                    {!disabled && (
                        <button
                            type="button"
                            className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); actions?.remove?.(); }}
                            aria-label={t('components.upload.delete') || 'Delete'}
                        >
                            <DeleteOutlined />
                        </button>
                    )}
                </div>
            );
        },
        [handlePreview, disabled]
    );

    const uploadButton = useMemo(() => (
        <button style={{ border: 0, background: 'none' }} type="button">
            {isUploading
                ? <LoadingOutlined style={{ fontSize: '26px', color: '#1677ff' }} />
                : <PlusOutlined />
            }
            <div style={{ marginTop: 8 }}>
                {isUploading ? t('components.upload.uploadIng') : t('components.upload.upload')}
            </div>
        </button>
    ), [isUploading]);

    return (
        <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            className={`rounded transition-colors ${isDragging ? 'bg-blue-50 ring-2 ring-blue-300 ring-dashed p-1' : ''}`}
        >
            <p className="text-xs text-gray-400 mb-2">{t('components.upload.dragOrPasteHint')}</p>
            <Upload
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleUploadChange}
                onRemove={handleRemove}
                showUploadList={{ showPreviewIcon: false, showRemoveIcon: false }}
                itemRender={renderUploadItem}
                maxCount={maxCount}
                multiple
                accept={accept}
                disabled={disabled || isUploading}
                beforeUpload={beforeUpload}
            >
                {fileList.length < maxCount ? uploadButton : null}
            </Upload>
            {/* 选择整个文件夹功能暂时关闭
            <input
                ref={folderInputRef}
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFolderChange}
                {...({ webkitdirectory: '' } as React.InputHTMLAttributes<HTMLInputElement>)}
            />
            <button
                type="button"
                className="text-xs text-blue-500 mt-1 hover:underline cursor-pointer bg-transparent border-0 p-0"
                onClick={() => folderInputRef.current?.click()}
            >
                {t('components.upload.selectFolder')}
            </button>
            */}
            {previewImage && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (v) => setPreviewOpen(v),
                        afterOpenChange: (v) => !v && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
        </div>
    );
};

export default ImageUploaderEnhance;
